import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Flag from "react-world-flags";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCheckCircle,
  FaDownload,
  FaPhone,
  FaLock,
  FaChevronDown,
  FaCalendarCheck,
} from "react-icons/fa";

/* ================= BRAND ================= */
const PRIMARY_GRADIENT =
  "bg-gradient-to-r from-primary-soft via-primary to-primary-soft bg-clip-text text-transparent animate-gradient";

/* ================= FAQ DATA ================= */
const FAQ_DATA = [
  {
    question: "Do I need prior knowledge of German to start?",
    answer:
      "No prior knowledge is required for A1 level. Our beginner program starts from scratch, covering pronunciation, basic vocabulary, and everyday expressions. Higher levels require a placement test.",
  },
  {
    question: "What exams does this course prepare me for?",
    answer:
      "Our German programs are aligned with Goethe-Institut standards and prepare students for Goethe-Zertifikat A1–B2 exams, widely accepted for study, work, and immigration.",
  },
  {
    question: "How long does each level take to complete?",
    answer:
      "A1 and A2 typically take 2–3 months, B1 around 3 months, and B2 up to 4 months depending on batch type (weekday or weekend).",
  },
  {
    question: "Are classes online or offline?",
    answer:
      "We offer both online live classes via Zoom and offline classroom sessions at selected locations. Online classes include recordings, live interaction, and practice activities.",
  },
  {
    question: "Will I receive a certificate after completion?",
    answer:
      "Yes, you will receive a course completion certificate from The Lingua Alliance. We also guide you thoroughly for official Goethe certification exams.",
  },
  {
    question: "What if I miss a class?",
    answer:
      "All sessions are recorded and available for lifetime access. You can also attend make-up classes subject to availability.",
  },
  {
    question: "Is German difficult to learn?",
    answer:
      "German grammar is structured and logical. With proper guidance and consistent practice, most learners find it very manageable and rewarding.",
  },
];

/* ================= COMPONENT ================= */
export default function German() {
  const formRef = useRef(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  /* Initialize AOS */
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
    });
  }, []);

  /* Sticky stop before footer */
  useEffect(() => {
    const handler = () => {
      const form = formRef.current;
      const footer = document.getElementById("page-footer");
      if (!form || !footer) return;

      const scrollY = window.pageYOffset;
      const footerTop = footer.getBoundingClientRect().top + scrollY;
      const formHeight = form.offsetHeight;
      const stopPoint = footerTop - formHeight - 24;

      if (scrollY >= stopPoint) {
        form.style.position = "absolute";
        form.style.top = `${stopPoint - form.parentElement.offsetTop}px`;
      } else {
        form.style.position = "sticky";
        form.style.top = "24px";
      }
    };

    window.addEventListener("scroll", handler);
    window.addEventListener("resize", handler);
    handler();

    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      {/* ================= HERO ================= */}
      <header className="relative min-h-[550px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?w=1600&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/50" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 text-white">
          <div className="max-w-3xl space-y-6">
            <div className="text-sm text-white/80">
              <Link to="/" className="hover:text-white">
                Home
              </Link>{" "}
              / Language Programs / <strong>German</strong>
            </div>

            <div className="flex items-center gap-3">
              <Flag code="DE" style={{ width: 40, height: 28 }} />
              <span className="text-xs uppercase tracking-widest text-white/80">
                Learn German with The Lingua Alliance
              </span>
            </div>

            <h1
              className={`text-4xl md:text-5xl pb-2 font-extrabold text-white`}
            >
              Goethe-Focused German Learning Tracks
            </h1>

            <p className="text-lg text-white/90 max-w-2xl">
              Structured German programs designed to take you from beginner to
              confident, fluent professional with globally recognized standards.
            </p>

            <div className="flex flex-wrap gap-4 pt-3">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <FaDownload className="text-lg" />
                Download Brochure
              </button>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-soft via-primary to-primary-soft text-white font-semibold rounded-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                <FaCalendarCheck className="text-lg" />
                Book Free Demo
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">11+</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Years of Expertise
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  4,000+
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Students Across Globe
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">✓</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Certified Experts
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  All Ages
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Programs Available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAIN GRID ================= */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_360px] gap-12">
        {/* ================= LEFT CONTENT ================= */}
        <main className="space-y-16">
          {/* COURSE LEVELS */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              German Language Program (Goethe Aligned)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <LevelItem
                title="A1 (Beginner) | 40 Hours"
                text="Begin with the fundamentals: greetings, self-introductions, numbers, pronunciation, and essential everyday vocabulary."
              />
              <LevelItem
                title="A2 (Elementary) | 40 Hours"
                text="Strengthen your foundation using frequently used expressions, basic dialogues, and core grammar concepts."
              />
              <LevelItem
                title="B1 (Intermediate) | 60 Hours"
                text="Engage in conversations about work, travel, and daily life. Understand routine written content and express opinions clearly."
              />
              <LevelItem
                title="B2 (Upper-Intermediate) | 80 Hours"
                text="Participate confidently in detailed discussions, understand complex content, and communicate naturally with native speakers."
              />
            </div>
          </section>

          {/* UNIQUE */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              What Makes Our German Program Unique
            </h2>

            <ul className="grid md:grid-cols-2 gap-4 text-gray-700">
              <ListItem text="Goethe-aligned curriculum with exam-focused training" />
              <ListItem text="Experienced trainers with real-world usage focus" />
              <ListItem text="Live interactive sessions with recordings" />
              <ListItem text="Structured grammar & speaking practice" />
              <ListItem text="Complete Goethe certification guidance" />
            </ul>
          </section>

          {/* COMPARISON */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              German Language: Difficulty Overview
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <Comparison
                label="Speaking"
                value="Moderate – Improves quickly with practice"
              />
              <Comparison
                label="Grammar"
                value="Structured – Logical and rule-based"
              />
              <Comparison
                label="Writing"
                value="Moderate – Clear sentence construction"
              />
              <Comparison
                label="Overall Difficulty"
                value="Moderate – Highly achievable with guidance"
              />
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h4>
                    <FaChevronDown
                      className={`text-primary transition-transform ${
                        expandedFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 border-t border-gray-100">
                      <p className="text-sm text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ================= RIGHT STICKY FORM ================= */}
        <aside className="relative">
          <div ref={formRef} className="hidden lg:block sticky top-24">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
              <div className="text-center mb-4">
                <h3 className={`text-2xl font-bold ${PRIMARY_GRADIENT}`}>
                  German Language Program
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  Goethe Prep · Live Classes · Certification Support
                </p>
              </div>

              <div className="text-center mb-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="text-4xl font-extrabold text-primary">
                  ₹60,000/-
                </div>
                <div className="text-sm text-gray-400 line-through">
                  ₹70,000/-
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  Weekday & Weekend Batches Available
                </div>
              </div>

              <form className="space-y-4">
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Full Name"
                />
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Email Address"
                />
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Mobile Number"
                />
                <button className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-primary-soft via-primary to-primary-soft">
                  Enroll Now
                </button>
              </form>

              <div className="mt-5 text-xs text-center text-gray-600">
                <div className="flex justify-center items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  30-Day Money-Back Guarantee
                </div>
              </div>

              <div className="mt-6 text-center pt-4 border-t">
                <a
                  href="tel:+919123408959"
                  className="inline-flex items-center gap-2 text-sm bg-blue-50 px-4 py-2 rounded-lg text-primary font-semibold"
                >
                  <FaPhone /> +91-9123408959
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function LevelItem({ title, text }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 shadow-md">
      <h4 className="font-bold mb-3">{title}</h4>
      <p className="text-sm text-gray-700">{text}</p>
    </div>
  );
}

function ListItem({ text }) {
  return (
    <li className="flex gap-2">
      <FaCheckCircle className="text-primary mt-1" />
      {text}
    </li>
  );
}

function Comparison({ label, value }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-gray-200 rounded-xl p-6 shadow-md">
      <strong className="block mb-2">{label}</strong>
      <div className="text-sm text-gray-600">{value}</div>
    </div>
  );
}
